import { inject, Injectable } from '@angular/core';
import { Environment } from '@rma/generic/util/environment';
import { FeatureFlags, TempFeatureFlags } from '@rma/generic/util/feature-flags';
import { combineLatest, map, Observable, of } from 'rxjs';
import { COLLECT_GROUP_FEATURES, getCollectGroupItems } from '../entities/nav-items-collect';
import {
  getManageGroupItems,
  getSettingsGroupItems,
  MANAGE_GROUP_FEATURES,
  SettingGroupFeature,
  SETTINGS_GROUP_FEATURES,
} from '../entities/nav-items-manage';
import { getOverview, getProfile, NavigationUserType } from '../entities/nav-items-misc';
import { getMonitorGroupItems, MonitorGroupFeature, MONITORING_GROUP_FEATURES } from '../entities/nav-items-monitor';
import { filterShareItems, getShareGroupItems, SHARE_GROUP_FEATURES } from '../entities/nav-items-share';
import { createNavigationGroup, NavigationGooglePermissions, NavigationGroup } from '../entities/side-nav-items';
import { SideNavigationConfigService } from './side-navigation-config.service';

@Injectable({
  providedIn: 'root',
})
export class TeamSideMenuService {
  private readonly config = inject(SideNavigationConfigService);
  private readonly environment = inject(Environment);

  public getNavigation(): Observable<NavigationGroup[]> {
    return combineLatest({
      enableDatafeeds: this.config.getLiveFeature(FeatureFlags.EnableDataFeeds, false),
      googlePermissions: this.config.getGoogleConfig(),
      hasSocialMedia: this.config.getSubscriptionAccess('HasSocialMedia'),
      marketReport: this.config.getMarketReportConfig(),
      showIntegrations: this.config.getLiveFeature(FeatureFlags.DashboardEnableIntegrations, false),
      showLeaderboards: of(false),
      showCsMenuItem: this.config.showSocialStudioLink$,
      displayBrandKits: this.config.getLiveFeature(TempFeatureFlags.BrandKitAdmin, false),
    }).pipe(
      map(({ enableDatafeeds, googlePermissions, marketReport, showIntegrations, showLeaderboards, showCsMenuItem, displayBrandKits }) => {
        const { baseUrl } = this.environment;
        const { hasMarketReportAccess, hideMarketReport } = marketReport;
        const userType: NavigationUserType = 'team';
        return [
          createNavigationGroup([getProfile()]),
          createNavigationGroup([getOverview()]),
          this.getCollectGroup(userType),
          this.getMonitorGroup(showLeaderboards),
          this.getManageItems(baseUrl, enableDatafeeds, displayBrandKits),
          this.getShareGroup(
            userType,
            showIntegrations,
            hideMarketReport,
            hasMarketReportAccess,
            googlePermissions,
            baseUrl,
            showCsMenuItem,
          ),
        ].filter((group) => group.items.length);
      }),
    );
  }

  private getCollectGroup(userType: NavigationUserType) {
    const notAvailable = ['pending', 'leasing'];
    const features = COLLECT_GROUP_FEATURES.filter((f) => !notAvailable.includes(f));
    const items = getCollectGroupItems(features, userType);
    return createNavigationGroup(items, 'collect');
  }

  private getShareGroup(
    userType: NavigationUserType,
    showIntergations: boolean,
    hideMarketReport: boolean,
    hasMarketReportAccess: boolean,
    googlePermissions: NavigationGooglePermissions,
    baseUrl: string,
    showCsMenuItem: boolean,
  ): NavigationGroup {
    const features = SHARE_GROUP_FEATURES.filter(filterShareItems(showIntergations, hideMarketReport, true, showCsMenuItem));
    return createNavigationGroup(getShareGroupItems(features, userType, hasMarketReportAccess, googlePermissions, baseUrl), 'share');
  }

  private getMonitorGroup(showLeaderboards: boolean): NavigationGroup {
    const notAvailable: MonitorGroupFeature[] = !showLeaderboards ? ['leaderboards'] : [];
    const features = MONITORING_GROUP_FEATURES.filter((f) => !notAvailable.includes(f));

    return createNavigationGroup(getMonitorGroupItems(features), 'monitor');
  }

  private getManageItems(baseUrl: string, enableDatafeeds: boolean, displayBrandKits: boolean): NavigationGroup {
    const features = MANAGE_GROUP_FEATURES.map((i) => i);

    return createNavigationGroup(
      [...getManageGroupItems(features), getSettingsGroupItems(this.getSettingsItems(enableDatafeeds, displayBrandKits), baseUrl)],
      'manage',
    );
  }

  private getSettingsItems(enableDatafeeds: boolean, displayBrandKits: boolean): SettingGroupFeature[] {
    const notAvailable: SettingGroupFeature[] = ['assistants'];

    if (!enableDatafeeds) {
      notAvailable.push('dataFeeds');
    }

    if (!displayBrandKits) {
      notAvailable.push('brandKits');
    }

    const items = SETTINGS_GROUP_FEATURES.filter((i) => !notAvailable.includes(i));
    return items;
  }
}
